import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpRight, Loader2, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { BRAND } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Diagnostic Sprint", "Transformation Programme", "CXO Advisory",
  "Cash Flow Optimisation", "Manufacturing Excellence", "Supply Chain Transformation",
  "Sales Effectiveness", "Other",
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", phone: "", interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message received. Vivek will get back within 48 hours.");
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-[1440px] px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <span className="eyebrow">Contact</span>
        <h1 className="mt-4 text-4xl lg:text-6xl tracking-[-0.03em] font-semibold text-slate-900 leading-[1.02]">
          Let&apos;s talk about <span className="text-slate-500">the one constraint</span> costing you cash.
        </h1>
        <p className="mt-6 text-[17px] text-slate-600 leading-[1.7] max-w-lg">
          Engagements are limited and hand-picked. Share a short brief — {BRAND.founder} personally responds within 48 hours,
          usually with a 20-minute call slot.
        </p>

        <div className="mt-10 space-y-4">
          <ContactRow Icon={MapPin} label="Based in" value={BRAND.city} />
          <ContactRow Icon={Mail} label="Founder" value={BRAND.email} href={`mailto:${BRAND.email}`} />
          <ContactRow Icon={Mail} label="Client services" value={BRAND.serviceEmail} href={`mailto:${BRAND.serviceEmail}`} />
          <ContactRow Icon={Phone} label="Direct line" value={BRAND.phone} href={`tel:${BRAND.phone.replace(/\s+/g,'')}`} />
        </div>

        <div className="mt-12 rounded-2xl bg-slate-900 text-white p-6">
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-400">Prefer to start with data?</div>
          <div className="mt-2 text-lg font-semibold">Run the 5-minute Diagnostic first.</div>
          <a href="/diagnostic" data-testid="contact-diagnostic-link" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 text-sm font-semibold px-4 py-2">
            Go to Diagnostic <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="ind-card p-6 lg:p-8">
          {done ? (
            <div data-testid="contact-success" className="py-16 text-center">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 mb-5">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900">Message received.</h2>
              <p className="mt-3 text-slate-600 max-w-md mx-auto">Thank you. Vivek will personally review your note and respond within 48 hours.</p>
              <button onClick={() => { setDone(false); setForm({ name: "", email: "", company: "", role: "", phone: "", interest: "", message: "" }); }} data-testid="contact-send-another" className="mt-6 text-sm text-slate-900 underline underline-offset-4">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField label="Your name*" testid="cf-name">
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Full name" data-testid="contact-name" required />
              </FormField>
              <FormField label="Email*" testid="cf-email">
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" data-testid="contact-email" required />
              </FormField>
              <FormField label="Company" testid="cf-company">
                <Input value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company name" data-testid="contact-company" />
              </FormField>
              <FormField label="Role / Title" testid="cf-role">
                <Input value={form.role} onChange={(e) => update("role", e.target.value)} placeholder="COO / CFO / VP Ops" data-testid="contact-role" />
              </FormField>
              <FormField label="Phone" testid="cf-phone">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 …" data-testid="contact-phone" />
              </FormField>
              <FormField label="Primary interest" testid="cf-interest">
                <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                  <SelectTrigger data-testid="contact-interest"><SelectValue placeholder="Pick a topic" /></SelectTrigger>
                  <SelectContent>
                    {INTERESTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Message*" testid="cf-msg" hint="Share a short paragraph on what you're trying to change and by when.">
                  <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} rows={6} placeholder="e.g. We're doing ₹450 Cr in metals aftermarket. Working capital has ballooned, OTIF is at 68%. Want to fix both in 6 months." data-testid="contact-message" required />
                </FormField>
              </div>
              <div className="md:col-span-2 flex items-center justify-between border-t border-slate-100 pt-6">
                <p className="text-xs text-slate-500 max-w-md">By submitting, you agree to be contacted by the Indusynq team regarding your enquiry.</p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-semibold px-6 py-3 hover:bg-black disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                  Send message
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function FormField({ label, hint, testid, children }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">{label}</Label>
      <div className="mt-2">{children}</div>
      {hint && <div className="mt-1.5 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function ContactRow({ Icon, label, value, href }) {
  const content = (
    <>
      <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">{label}</div>
        <div className="text-[15px] text-slate-900 font-medium">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-center gap-4 hover:opacity-80 transition-opacity">{content}</a>
  ) : (
    <div className="flex items-center gap-4">{content}</div>
  );
}
